/**
 * Barra de ações em massa para seleção múltipla.
 */
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XIcon } from '../icons';
import { AppIcon } from '../icons/AppIcon';
import { useClickOutside } from '../../hooks/useClickOutside';
import { cn } from '@/lib/utils';

export interface BulkActionOption {
    id: string;
    label: string;
}

export interface BulkAction {
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    onClick?: () => void;
    variant?: 'primary' | 'danger';
    type?: 'button' | 'dropdown';
    options?: BulkActionOption[];
    onSelectOption?: (optionId: string) => void;
}

interface BulkActionsBarProps {
    selectedCount: number;
    actions: BulkAction[];
    onClose: () => void;
    label?: string;
}

function actionButtonClass(variant: BulkAction['variant'] = 'primary') {
    return cn(
        'flex items-center gap-2 whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-colors',
        variant === 'danger'
            ? 'bg-destructive/10 text-destructive hover:bg-destructive/20'
            : 'bg-primary/10 text-primary hover:bg-primary/20',
    );
}

const BulkDropdown: React.FC<{
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    options: BulkActionOption[];
    onSelect: (optionId: string) => void;
    variant?: BulkAction['variant'];
}> = ({ label, icon: Icon, options, onSelect, variant = 'primary' }) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);
    useClickOutside(ref, () => setIsOpen(false), isOpen);

    return (
        <div ref={ref} className="relative">
            <button type="button" onClick={() => setIsOpen(open => !open)} className={actionButtonClass(variant)}>
                <Icon className="size-4" />
                <span>{label}</span>
                <AppIcon
                    name="chevron-down"
                    className={cn('size-3.5 transition-transform', isOpen && 'rotate-180')}
                />
            </button>
            {isOpen && (
                <div className="absolute bottom-full left-0 z-[210] mb-2 min-w-[200px] rounded-xl border border-border bg-card py-1 shadow-lg">
                    {options.map(option => (
                        <button
                            key={option.id}
                            type="button"
                            onClick={() => {
                                onSelect(option.id);
                                setIsOpen(false);
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted"
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
};

const BulkActionsBar: React.FC<BulkActionsBarProps> = ({
    selectedCount,
    actions,
    onClose,
    label = 'Selecionados',
}) => {
    const [host, setHost] = useState<HTMLElement | null>(null);

    useEffect(() => {
        setHost(document.body);
    }, []);

    if (selectedCount === 0 || !host) return null;

    return createPortal(
        <div
            className="pointer-events-none fixed bottom-[max(5.75rem,calc(env(safe-area-inset-bottom)+5rem))] left-1/2 z-[200] w-max max-w-[calc(100%-2rem)] -translate-x-1/2 md:bottom-6"
            role="toolbar"
            aria-label="Ações da seleção"
        >
            <div className="pointer-events-auto flex w-max max-w-full items-center gap-s3 rounded-2xl border border-border bg-card px-s4 py-s3 shadow-lg">
                <div className="flex min-w-0 items-center gap-s3">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-primary-foreground shadow-sm">
                        {selectedCount > 99 ? '99+' : selectedCount}
                    </div>
                    <span className="hidden truncate text-sm font-medium text-foreground md:inline">
                        {label}
                    </span>
                </div>

                <div className="h-7 w-px shrink-0 bg-border" />

                <div className="flex items-center gap-1.5">
                    {actions.map((action, index) => {
                        const Icon = action.icon;
                        const isDropdown = action.type === 'dropdown' && action.options && action.onSelectOption;

                        if (isDropdown) {
                            return (
                                <BulkDropdown
                                    key={index}
                                    label={action.label}
                                    icon={Icon}
                                    options={action.options!}
                                    onSelect={action.onSelectOption!}
                                    variant={action.variant}
                                />
                            );
                        }

                        return (
                            <button
                                key={index}
                                type="button"
                                onClick={action.onClick}
                                className={actionButtonClass(action.variant)}
                            >
                                <Icon className="size-4" />
                                <span>{action.label}</span>
                            </button>
                        );
                    })}
                </div>

                <button
                    type="button"
                    onClick={onClose}
                    className="ml-1 rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    aria-label="Fechar seleção"
                >
                    <XIcon className="size-4" />
                </button>
            </div>
        </div>,
        host,
    );
};

export default BulkActionsBar;
